import { Link, useParams } from 'react-router-dom';
import {
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import Navbar from '../../components/Navbar';
import Chatbot from '../../components/Chatbot';

export default function FlowDescriptionPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            ← Back to Help Center
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">Adding Flow Descriptions</h1>
          <p className="text-lg text-gray-600 mt-2">
            Why documenting your flows makes them easier to maintain
          </p>
        </div>

        {/* Info Alert */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-8">
          <div className="flex">
            <InformationCircleIcon className="h-6 w-6 text-blue-400 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">Documentation Best Practice</h3>
              <p className="mt-2 text-sm text-blue-700">
                Flow descriptions help your team understand what a flow does, when it runs, and why it exists
                - without having to open and analyze the flow logic.
              </p>
            </div>
          </div>
        </div>

        {/* What is it? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What Is a Flow Description?</h2>
          <p className="text-gray-700 mb-4">
            A flow description is text that explains the purpose and behavior of your flow. It appears
            in Flow Builder and in Salesforce Setup, helping administrators and developers understand
            what the flow does without opening it.
          </p>

          <div className="mt-4 p-4 bg-green-50 rounded border border-green-200">
            <p className="text-sm font-semibold text-green-900 mb-2">Example of a good description:</p>
            <p className="text-sm text-green-800 italic">
              "This flow automatically updates the Account's Last Activity Date when a new Task
              is completed. It runs on Task creation and update (after-save trigger) and only
              processes Tasks with Status = 'Completed'. The flow uses bulkified collection
              processing to handle multiple tasks efficiently."
            </p>
          </div>

          <div className="mt-4 p-4 bg-red-50 rounded border border-red-200">
            <p className="text-sm font-semibold text-red-900 mb-2">Example of a missing/poor description:</p>
            <p className="text-sm text-red-800 italic">
              (blank) or "Updates accounts" or "Task flow"
            </p>
          </div>
        </div>

        {/* Why is it important? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Are Descriptions Important?</h2>

          <div className="space-y-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-900">Faster Troubleshooting</h3>
              <p className="text-gray-700 mt-1">
                When a flow fails or behaves unexpectedly, a good description helps you quickly
                understand what it's supposed to do, making debugging faster.
              </p>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-gray-900">Team Collaboration</h3>
              <p className="text-gray-700 mt-1">
                New team members or colleagues can understand your flows without having to analyze
                dozens of elements. This prevents duplicating existing automation.
              </p>
            </div>

            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="font-semibold text-gray-900">Audit and Compliance</h3>
              <p className="text-gray-700 mt-1">
                During audits or documentation reviews, descriptions provide quick context about
                automation rules without deep technical analysis.
              </p>
            </div>

            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="font-semibold text-gray-900">Prevents Duplication</h3>
              <p className="text-gray-700 mt-1">
                Before building new automation, admins can search flow descriptions to see if
                similar logic already exists.
              </p>
            </div>
          </div>
        </div>

        {/* How to add */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Add a Flow Description</h2>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
            <div className="flex">
              <LightBulbIcon className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-green-800">Quick Fix</h3>
                <p className="mt-2 text-sm text-green-700">
                  Adding a description takes less than 2 minutes and dramatically improves flow maintainability.
                </p>
              </div>
            </div>
          </div>

          <h3 className="font-semibold text-gray-900 mb-3">Step-by-Step:</h3>

          <ol className="space-y-4">
            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  1
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Open Flow in Flow Builder</h4>
                  <p className="text-gray-700 mt-1 text-sm">
                    Navigate to Setup → Flows → Click on your flow → Open in Flow Builder
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  2
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Click "Edit Properties"</h4>
                  <p className="text-gray-700 mt-1 text-sm">
                    In the Toolbox on the left, click "Edit Properties" or click the gear icon next to the flow name
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  3
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Add Description</h4>
                  <p className="text-gray-700 mt-1 text-sm">
                    In the "Description" field, add a clear explanation of what the flow does
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  4
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Save the Flow</h4>
                  <p className="text-gray-700 mt-1 text-sm">
                    Click "Done" then "Save"
                  </p>
                </div>
              </div>
            </li>
          </ol>
        </div>

        {/* What to include */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What to Include in Your Description</h2>

          <div className="space-y-4">
            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Purpose</h3>
                <p className="text-gray-700 text-sm mt-1">
                  What does this flow accomplish? What business problem does it solve?
                </p>
                <p className="text-gray-600 text-sm italic mt-1">
                  Example: "Automates opportunity renewal process"
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Trigger</h3>
                <p className="text-gray-700 text-sm mt-1">
                  When does this flow run? What causes it to execute?
                </p>
                <p className="text-gray-600 text-sm italic mt-1">
                  Example: "Runs when Opportunity Stage changes to 'Closed Won'"
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Key Actions</h3>
                <p className="text-gray-700 text-sm mt-1">
                  What are the main things this flow does?
                </p>
                <p className="text-gray-600 text-sm italic mt-1">
                  Example: "Creates renewal opportunity, copies products, sends notification to account owner"
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Important Conditions (Optional)</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Are there important filter criteria or conditions?
                </p>
                <p className="text-gray-600 text-sm italic mt-1">
                  Example: "Only processes opportunities with Annual Contract Value &gt; $10,000"
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Business Context (Optional)</h3>
                <p className="text-gray-700 text-sm mt-1">
                  Why was this flow created? Who requested it?
                </p>
                <p className="text-gray-600 text-sm italic mt-1">
                  Example: "Implements sales team requirement from Q2 2024 kickoff"
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Before/After Example */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Before & After Example</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Before */}
            <div className="border-2 border-red-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <XCircleIcon className="h-6 w-6 text-red-500 mr-2" />
                <h3 className="font-semibold text-red-700">Before (No Description)</h3>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-semibold text-gray-700">Flow Name:</p>
                <p className="text-sm text-gray-900 font-mono">Account_Update_Flow</p>
                <p className="text-sm font-semibold text-gray-700 mt-3">Description:</p>
                <p className="text-sm text-gray-400 italic">(blank)</p>
              </div>
              <div className="mt-3 text-sm text-red-700">
                Result: No one knows what this flow does without opening it
              </div>
            </div>

            {/* After */}
            <div className="border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="font-semibold text-green-700">After (With Description)</h3>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-semibold text-gray-700">Flow Name:</p>
                <p className="text-sm text-gray-900 font-mono">Account_Update_Flow</p>
                <p className="text-sm font-semibold text-gray-700 mt-3">Description:</p>
                <p className="text-sm text-gray-900">
                  "Updates Account billing address when primary contact address changes. Runs
                  after-save on Contact when Mailing Address fields change. Only updates if
                  Contact.Is_Primary__c = true."
                </p>
              </div>
              <div className="mt-3 text-sm text-green-700">
                Result: Anyone can understand the flow's purpose instantly
              </div>
            </div>
          </div>
        </div>

        {/* Template */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Description Template</h2>
          <p className="text-gray-700 mb-4">
            Use this template to write clear, comprehensive flow descriptions:
          </p>

          <div className="bg-blue-50 border border-blue-200 p-4 rounded">
            <p className="text-sm text-blue-900 font-mono leading-relaxed">
              This flow [PURPOSE: what it does] when [TRIGGER: what causes it to run].
              It [KEY ACTIONS: main operations], and [CONDITIONS: important filters or rules if applicable].
              [BUSINESS CONTEXT: why it exists or who requested it, if relevant].
            </p>
          </div>

          <div className="mt-4 bg-gray-50 p-4 rounded">
            <p className="text-sm font-semibold text-gray-900 mb-2">Example using template:</p>
            <p className="text-sm text-gray-700">
              This flow <span className="text-blue-600">creates renewal opportunities</span> when
              <span className="text-blue-600"> an opportunity reaches 90 days before Close Date</span>.
              It <span className="text-blue-600">copies all products from the original opportunity,
              sends an email to the account owner, and creates a task reminder</span>, and
              <span className="text-blue-600"> only runs for opportunities with Type = 'New Business'
              and Amount &gt; $50,000</span>. <span className="text-blue-600">Built to automate
              the renewal process requested by Sales Ops in Q1 2024</span>.
            </p>
          </div>
        </div>

        {/* Impact */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
          <h2 className="text-xl font-bold text-blue-900 mb-2">Expected Benefits</h2>
          <div className="text-blue-800 space-y-2">
            <p className="font-semibold">Time Saved: Hours per month</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Reduce time spent understanding existing flows</li>
              <li>Faster troubleshooting when issues occur</li>
              <li>Better team collaboration and knowledge transfer</li>
              <li>Prevent duplicate automation</li>
            </ul>
          </div>
        </div>

        {/* Related Resources */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
          <ul className="space-y-3">
            <li>
              <Link to={`${basePath}/help/flow-naming`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Naming Conventions →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Best practices for naming your flows</p>
            </li>
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Best Practices →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Complete guide to building high-quality flows</p>
            </li>
            <li>
              <Link to={`${basePath}/help/flow-complexity`} className="text-blue-600 hover:text-blue-800 font-medium">
                Managing Flow Complexity →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Keeping flows maintainable as they grow</p>
            </li>
          </ul>
        </div>
      </div>

      <Chatbot />
    </div>
  );
}
